import type { WebChartDataRow, WebChartRequest } from '../llmWebTools';

export type ChartPreviewDataRow = Record<string, string | number>;

export type ChartPreviewRequest = Omit<WebChartRequest, 'data' | 'categoryField' | 'valueFields'> & {
  categoryField: string;
  valueFields: string[];
  data: ChartPreviewDataRow[];
};

type CategoryAxisLabelLayout = {
  rotateLabels: boolean;
  minGridDistance: number;
  maxLabelWidth: number;
};

type CategoryAxisLabelLayoutParams = {
  data: ChartPreviewDataRow[];
  categoryField: string;
  containerWidth: number;
  maxLabelWidth?: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const extractValueFields = (data: WebChartDataRow[]): string[] => {
  const fields = new Set<string>();
  for (const item of data) {
    const { values } = item;
    if (!Array.isArray(values)) continue;
    for (const entry of values) {
      if (!isRecord(entry)) continue;
      if (typeof entry.field === 'string') {
        fields.add(entry.field);
      }
    }
  }
  return Array.from(fields);
};

export const normalizeChartRequest = (chart: WebChartRequest): ChartPreviewRequest => {
  const categoryField = chart.categoryField || 'category';
  const rawData = Array.isArray(chart.data) ? chart.data : [];
  const valueFields =
    Array.isArray(chart.valueFields) && chart.valueFields.length ? chart.valueFields : extractValueFields(rawData);

  const data = rawData
    .map((item) => {
      if (!isRecord(item)) return null;

      const row: ChartPreviewDataRow = {};
      const categoryValue = item[categoryField] ?? item.category;
      if (typeof categoryValue === 'string' || typeof categoryValue === 'number') {
        row[categoryField] = categoryValue;
      }

      if (Array.isArray(item.values)) {
        for (const entry of item.values) {
          if (!isRecord(entry)) continue;
          const { field, value } = entry;
          if (typeof field === 'string' && typeof value === 'number') {
            row[field] = value;
          }
        }
      } else {
        for (const field of valueFields) {
          const value = item[field];
          if (typeof value === 'number') {
            row[field] = value;
          }
        }
      }

      return Object.keys(row).length > 0 ? row : null;
    })
    .filter((row): row is ChartPreviewDataRow => row !== null);

  return {
    ...chart,
    categoryField,
    valueFields,
    data,
  };
};

export const getCategoryAxisLabelLayout = (params: CategoryAxisLabelLayoutParams): CategoryAxisLabelLayout => {
  const { data, categoryField, containerWidth, maxLabelWidth = 90 } = params;
  const safeWidth = Number.isFinite(containerWidth) ? containerWidth : 0;
  const labelLengths = data.map((row) => String(row[categoryField] ?? '').length);
  const averageLabelLength =
    labelLengths.length > 0 ? labelLengths.reduce((sum, length) => sum + length, 0) / labelLengths.length : 0;
  const longestLabelLength = labelLengths.length > 0 ? Math.max(...labelLengths) : 0;
  const widthPerLabel = safeWidth > 0 && data.length > 0 ? safeWidth / data.length : 0;
  const estimatedLongestLabelWidth = longestLabelLength * 7;
  const needsRotationForSpace = widthPerLabel > 0 && widthPerLabel < Math.min(70, estimatedLongestLabelWidth + 10);
  const rotateLabels = averageLabelLength > 10 || data.length > 6 || needsRotationForSpace;
  const computedMaxWidth =
    widthPerLabel > 0 ? Math.min(maxLabelWidth, Math.max(24, Math.floor(widthPerLabel - 6))) : maxLabelWidth;

  return {
    rotateLabels,
    minGridDistance: rotateLabels ? 30 : 20,
    maxLabelWidth: computedMaxWidth,
  };
};
